import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Text,
  VStack,
  SimpleGrid,
  Button,
  useToast,
  useColorModeValue,
  Divider,
  Flex,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import Inputs from "./InputComponent/Inputs";
import {
  getFormStructure,
  getExistingData,
  saveFormData,
  getFormStructureDetails,
} from "../../InventoryServices/InventoryInfoTabs/InfoTabsAPI";
import { mapRsformDataToFormValues } from "../../InventoryServices/InventoryInfoTabs/rsformDataMapper";

const InfoTab = () => {
  const { id } = useParams();
  const [formFields, setFormFields] = useState([]);
  const [formSections, setFormSections] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [existingDataId, setExistingDataId] = useState(null);
  const [formTitle, setFormTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const bg = useColorModeValue("white", "gray.800");
  const buttonBg = useColorModeValue("blue.600", "blue.500");
  const headingColor = useColorModeValue("gray.800", "whiteAlpha.900");
  const hoverBg = useColorModeValue("blue.700", "blue.600");
  const sectionBg = useColorModeValue("gray.50", "gray.700");
  const accordionBg = useColorModeValue("blue.50", "gray.700");
  const accordionHoverBg = useColorModeValue("blue.100", "gray.600");

  useEffect(() => {
    let isMounted = true;

    const fetchFormData = async () => {
      try {
        setLoading(true);

        const formData = await getFormStructure(id);
        if (isMounted && formData.status && formData.data.form) {
          setFormFields(formData.data.form.fields || []);
          setFormSections(formData.data.form.sections || []);
          setFormTitle(formData.data.form.title || "");

          const allFields = [
            ...formData.data.form.fields,
            ...formData.data.form.sections.flatMap((section) => section.fields || []),
          ];

          const processCheckboxFields = (data) => {
            const processed = { ...data };
            allFields.forEach((field) => {
              if (field.type === "checkbox" && processed[field.key]) {
                if (!Array.isArray(processed[field.key])) {
                  processed[field.key] = [processed[field.key]];
                }
              }
            });
            return processed;
          };

          const existing = await getExistingData(id);
          console.log("[InfoTab] existing API response:", existing);
          console.log("[InfoTab] existing.status:", existing.status, "| existing.data:", existing.data);

          if (isMounted && existing.status && existing.data) {
            // Existing saved data found → use it directly
            console.log("[InfoTab] Using EXISTING saved data");
            setFormValues(processCheckboxFields(existing.data));
            setExistingDataId(existing.data.id);
          } else {
            // No saved data → prefill from rsform_data via details API
            console.log("[InfoTab] No existing data, fetching details for prefill...");
            const details = await getFormStructureDetails(id);
            console.log("[InfoTab] details API response:", details);
            if (isMounted && details.status && details.data?.rsform_data) {
              const formTitle = formData.data.form?.title || "";
              console.log("[InfoTab] formTitle:", formTitle);
              const mappedValues = mapRsformDataToFormValues(details.data.rsform_data, formTitle);
              console.log("[InfoTab] mappedValues from mapper:", mappedValues);
              setFormValues(processCheckboxFields(mappedValues));
            }
          }
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
        if (isMounted) {
          toast({
            title: "Error",
            description: "Failed to load form data",
            status: "error",
            duration: 2000,
            isClosable: true,
          });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (id) fetchFormData();

    return () => {
      isMounted = false;
    };
  }, [id, toast]);

  const handleInputChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    const allFields = [
      ...formFields,
      ...formSections.flatMap((section) => section.fields || []),
    ];
  
    const missingFields = allFields
      .filter((field) => field.required && !formValues[field.key])
      .map((field) => field.label);
  
    if (missingFields.length > 0) {
      toast({
        title: "Validation Error",
        description: `Please fill in: ${missingFields.join(", ")}`,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
  
    // Force plot_level_difference = 0 if plot_level = "Same as Road"
    const finalValues = {
      ...formValues,
      ...(formValues["plot_level"] === "Same as Road"
        ? { plot_level_difference: 0 }
        : {}),
    };
    console.log("finalValues", finalValues)
  
    try {
      const result = await saveFormData(id, finalValues, existingDataId);
  
      toast({
        title: existingDataId ? "Data Updated" : "Data Submitted",
        description: "Form data saved successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
  
      if (!existingDataId) {
        setExistingDataId(result.data?.id);
      }
    } catch (error) {
      console.error("Error saving form data:", error);
      toast({
        title: "Error",
        description: "Failed to save form data",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Flex minH="300px" align="center" justify="center">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
      </Flex>
    );
  }

  return (
    <Box bg={bg} p={8} borderRadius="lg" border="1px solid" borderColor="gray.200" shadow="sm">
      <Text fontSize="2xl" fontWeight="bold" mb={2} color={headingColor}>
        Product Information
      </Text>
      <Divider mb={6} />

      <VStack spacing={10} align="stretch">
        {formSections.length > 0 ? (
          <Accordion
            allowMultiple
            defaultIndex={Array.from({ length: formSections.length }, (_, i) => i)}
          >
            {formSections.map((section, sectionIndex) => {
              let filteredFields = section.fields || [];

              // Condition 1: Plot Shape → Section 3 filtering
              // V1 ("floor-plan"): section 3 = Plot Size — filter by shape
              // V2 ("floor-plan-v2"): section 3 = Define Surroundings — show all fields
              if (section.section_number === 3 && formTitle !== "floor-plan-v2") {
                const shape = formValues["plot_shape"];
              
                filteredFields = (section.fields || []).filter((f) => {
                  if (f.key === "plot_dimension_correct") return true;
              
                  if (shape === "rectangular") {
                    return f.key === "width" || f.key === "depth";
                  } else if (shape === "non_rectangular") {
                    return (
                      f.key === "front_width" ||
                      f.key === "back_width" ||
                      f.key === "left_depth" ||
                      f.key === "right_depth"
                    );
                  }
                  return false;
                });
              }

              // Condition 2: Plot Level → Section 6 (V1 only)
              // V1 section 6 = Plot Level Info — hide level_difference if "Same as Road"
              // V2 section 6 = Plot Dimensions — no filter
              if (section.section_number === 6 && formTitle !== "floor-plan-v2") {
                const plotLevel = formValues["plot_level"];
                if (plotLevel === "Same as Road") {
                  filteredFields = filteredFields.filter(
                    (f) => f.key !== "plot_level_difference"
                  );
                }
              }
              // V2: section 7 = Plot Level Info — hide levelDelta if levelRelation = "same"
              if (section.section_number === 7 && formTitle === "floor-plan-v2") {
                const levelRelation = formValues["levelRelation"];
                if (levelRelation === "same") {
                  filteredFields = filteredFields.filter(
                    (f) => f.key !== "levelDelta"
                  );
                }
              }

              // Condition 3: Elevation Preference -> Section 19
              if (section.section_number === 19) {
                const preferredStyle = formValues["preferred_style"];
                if (preferredStyle !== "other") {
                  filteredFields = filteredFields.filter(
                    (f) => f.key !== "preferred_style_other"
                  );
                }
              }

              return (
                <AccordionItem
                  key={section.section_number || sectionIndex}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  mb={4}
                >
                  <AccordionButton
                    bg={accordionBg}
                    _hover={{ bg: accordionHoverBg }}
                    borderRadius="md"
                    p={4}
                  >
                    <Box flex="1" textAlign="left">
                      <Text fontSize="lg" fontWeight="semibold" color={headingColor}>
                        {section.section_number}. {section.section_title}
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>

                  <AccordionPanel pb={6} bg={sectionBg}>
                    <SimpleGrid
                      columns={{ base: 1, md: 2 }}
                      spacingX={6}
                      spacingY={4}
                    >
                      {filteredFields.map((field) => (
                        <Inputs
                          key={field.key}
                          field={field}
                          value={formValues[field.key]}
                          onChange={handleInputChange}
                        />
                      ))}
                    </SimpleGrid>
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Accordion>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={10} spacingY={6}>
            {formFields?.map((field) => (
              <Inputs
                key={field.id || field.key}
                field={field}
                value={formValues[field.key]}
                onChange={handleInputChange}
              />
            ))}
          </SimpleGrid>
        )}

        <Button
          bg={buttonBg}
          color="white"
          px={8}
          py={6}
          rounded="lg"
          fontWeight="semibold"
          fontSize="lg"
          shadow="md"
          _hover={{ bg: hoverBg, transform: "translateY(-2px)", shadow: "lg" }}
          _active={{ transform: "translateY(0)" }}
          onClick={handleSubmit}
          alignSelf="flex-end"
        >
          {existingDataId ? "Update" : "Save"}
        </Button>
      </VStack>
    </Box>
  );
};

export default InfoTab;